import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getMemoryBookList, postMemoryBookInitiate, updateMemoryBookShareStatus, getMemoryBookSingle } from "../../../services/memoryBookService";
import { getCurrentPackageUserModule, getUUID } from "../../../services/api";
import MemoryBookInside from "./memoryBookInside";
import ModuleHeader from "../../moduleHeader";
import { FaCheck, FaTimes } from 'react-icons/fa';
import { FiBook, FiPlus, FiChevronRight, FiFolder, FiTrash2, FiEdit3 } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import Notiflix from "notiflix";

export default function MemoryBook({ id, moduleId, isOwner }) {
  const { id: routeId } = useParams();
  const actualId = id || routeId;

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUuid, setSelectedUuid] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [packageData, setPackageData] = useState(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [newBookTitle, setNewBookTitle] = useState("");
  const [newBookDescription, setNewBookDescription] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [updatingShare, setUpdatingShare] = useState(null);

  const fetchList = async () => {
    try {
      setLoading(true);
      const response = await getMemoryBookList();
      if (response && response.success && response.data) {
        // API direkt olarak data içinde array döndürüyor
        const allBooks = Array.isArray(response.data) ? response.data : [];
        setList(allBooks);
      } else {
        setList([]);
      }
    } catch (err) {
      setError("Liste alınamadı");
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  const createMemoryBook = async () => {
    if (!newBookTitle.trim()) {
      Notiflix.Notify.failure("Lütfen başlık giriniz!");
      return;
    }

    try {
      setCreating(true);
      const response = await postMemoryBookInitiate({
        title: newBookTitle.trim(),
        description: newBookDescription.trim() || ""
      });
      if (response && response.success) {
        Notiflix.Notify.success("Anı defteri başarıyla oluşturuldu!");
        setShowCreatePopup(false);
        setNewBookTitle("");
        setNewBookDescription("");
        // Yeni oluşturulan defteri listeye ekle
        await fetchList();
      }
    } catch (err) {
      console.error("Anı defteri oluşturulurken hata:", err);
      Notiflix.Notify.failure(
        err.response?.data?.message || "Anı defteri oluşturulamadı"
      );
    } finally {
      setCreating(false);
    }
  };

  // Paket verisini çek
  useEffect(() => {
    if (!actualId) return;

    const fetchPackageData = async () => {
      try {
        const data = await getCurrentPackageUserModule(actualId, 10); // MemoryBook moduleId: 10
        setPackageData(data);
      } catch (error) {
        console.error("Paket verisi alınamadı:", error);
      } finally {
        setPageLoading(false);
      }
    };

    fetchPackageData();
  }, [actualId]);

  useEffect(() => {
    fetchList();
  }, []);

  const openBook = async (uuid) => {
    setSelectedUuid(uuid);
    setDetail(null);
    setDetailLoading(true);
    try {
      const response = await getMemoryBookSingle(uuid);
      if (response && response.success && response.data) {
        setDetail(response.data);
      } else {
        setDetail({ uuid: uuid }); // En azından uuid'yi set et
      }
    } catch (e) {
      console.error("Anı defteri detayı alınırken hata:", e);
      setDetail({ uuid: uuid }); // Hata durumunda da uuid'yi set et
    } finally {
      setDetailLoading(false);
    }
  };

  if (selectedUuid) {
    if (detailLoading) {
      return (
        <div className="min-h-screen bg-[#e8e9ed] px-4 py-6 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8] rounded-3xl flex items-center justify-center mx-auto mb-4 animate-pulse">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div className="inline-flex items-center space-x-2 text-gray-600">
              <div className="w-4 h-4 border-2 border-[#3DA7B5] border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm font-medium">Anı defteri açılıyor...</span>
            </div>
          </div>
        </div>
      );
    }
    return <MemoryBookInside data={detail} onBack={() => setSelectedUuid(null)} />;
  }

  // Loading screen
  if (pageLoading || !packageData) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#fff]">
        <div className="flex flex-col items-center justify-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              ></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
            </div>
          </div>
          <p className="text-xl font-semibold text-gray-800 animate-pulse">
            Yükleniyor...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
        {/* ModuleHeader */}
        {packageData && (
          <div className="relative z-30 w-full mb-2">
            <ModuleHeader
              userName={
                packageData.user?.first_name + " " + packageData.user?.last_name
              }
              subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
              title={packageData.package?.name}
              description={packageData.package?.description}
              backgroundImage={packageData.package?.image_url}
              onExpandedChange={setIsHeaderExpanded}
              showToggle={true}
              autoHeight={true}
            />
          </div>
        )}

        <div className="w-full px-5 py-6">
          <div className="max-w-sm mx-auto">

            {/* Yeni defter oluşturma kartı */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative mb-8"
            >
              <button
                onClick={() => setShowCreatePopup(true)}
                className="w-full group relative overflow-hidden bg-gradient-to-br from-[#3DA7B5] to-[#2A8B96] text-white rounded-2xl p-0 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-150 transition-transform duration-500">
                  <FiBook className="w-24 h-24 text-white" />
                </div>

                <div className="relative z-10 flex items-center justify-between p-6">
                  <div className="text-left">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mb-3 group-hover:bg-white/30 transition-colors">
                      <FiPlus className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-1">Yeni Anı Defteri</h3>
                    <p className="text-white/80 text-sm font-medium">Özel anılarınızı biriktirin</p>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-white text-[#3DA7B5] flex items-center justify-center opacity-0 group-hover:opacity-100 transform translate-x-4 group-hover:translate-x-0 transition-all duration-300">
                    <FiChevronRight className="w-5 h-5" />
                  </div>
                </div>

                {/* Shimmer Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
              </button>
            </motion.div>

            {/* Create Popup Modal */}
            <AnimatePresence>
              {showCreatePopup && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                  onClick={() => setShowCreatePopup(false)}
                >
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl overflow-hidden relative"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8]" />

                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-full bg-[#3DA7B5]/10 flex items-center justify-center">
                        <FiEdit3 className="w-5 h-5 text-[#3DA7B5]" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-800">Yeni Anı Defteri</h3>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                          Başlık <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={newBookTitle}
                          onChange={(e) => setNewBookTitle(e.target.value)}
                          placeholder="Örn: 2024 Yaz Tatili"
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#3DA7B5] focus:border-transparent outline-none text-gray-800 transition-all placeholder:text-gray-400 font-medium"
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                          Açıklama <span className="text-gray-400 font-normal normal-case">(İsteğe bağlı)</span>
                        </label>
                        <textarea
                          value={newBookDescription}
                          onChange={(e) => setNewBookDescription(e.target.value)}
                          placeholder="Bu anı defteri hakkında kısa bir not..."
                          rows={3}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#3DA7B5] focus:border-transparent outline-none text-gray-800 transition-all resize-none placeholder:text-gray-400 font-medium"
                        />
                      </div>

                      <div className="flex gap-3 pt-2">
                        <button
                          onClick={() => {
                            setShowCreatePopup(false);
                            setNewBookTitle("");
                            setNewBookDescription("");
                          }}
                          className="flex-1 py-3 px-4 border border-gray-200 text-gray-600 rounded-xl font-semibold hover:bg-gray-50 hover:text-gray-800 transition-colors"
                        >
                          İptal
                        </button>
                        <button
                          onClick={createMemoryBook}
                          disabled={creating || !newBookTitle.trim()}
                          className="flex-1 py-3 px-4 bg-[#3DA7B5] text-white rounded-xl font-semibold hover:bg-[#2A8B96] transition-all shadow-lg shadow-[#3da7b5]/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                        >
                          {creating ? (
                            <span className="flex items-center justify-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                              <span className="text-sm">Oluşturuluyor</span>
                            </span>
                          ) : (
                            "Oluştur"
                          )}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Liste */}
            <div className="space-y-4 pb-32">
              <h3 className="text-lg font-bold text-gray-800 pl-1 flex items-center gap-2">
                <FaCheck className="w-4 h-4 text-[#3DA7B5]" />
                Defterlerim
              </h3>

              {loading && (
                <div className="text-center py-12 bg-white rounded-2xl border border-gray-100 shadow-sm">
                  <div className="inline-flex items-center space-x-2 text-gray-500">
                    <div className="w-5 h-5 border-2 border-[#3DA7B5] border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm font-medium">Anılarınız yükleniyor...</span>
                  </div>
                </div>
              )}

              {error && !loading && (
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center"
                >
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FaTimes className="w-5 h-5 text-red-500" />
                  </div>
                  <p className="text-red-600 text-sm font-medium mb-1">Bir şeyler ters gitti</p>
                  <p className="text-red-500/80 text-xs">{error}</p>
                </motion.div>
              )}

              {!loading && !error && list.length === 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12 px-6 bg-white rounded-3xl border border-dashed border-gray-200"
                >
                  <div className="w-20 h-20 bg-blue-50/50 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                    <div className="absolute inset-0 bg-blue-100 rounded-full animate-ping opacity-20" />
                    <FiFolder className="w-10 h-10 text-[#3DA7B5] opacity-50" />
                  </div>
                  <h3 className="text-gray-800 font-bold mb-2">Henüz anı defteri yok</h3>
                  <p className="text-gray-500 text-sm max-w-[200px] mx-auto">İlk anı defterinizi oluşturmak için yukarıdaki renkli butona tıklayın</p>
                </motion.div>
              )}

              <AnimatePresence>
                {!loading &&
                  !error &&
                  list.map((item, index) => {
                    // Pending durumunu kontrol et - is_shared true ve share_status "pending" ise
                    const isPending = item.is_shared === true && item.share_status === "pending";
                    // share_id'yi shared_with array'inden al (pending durumundaki ilk item'dan)
                    const pendingShare = isPending && Array.isArray(item.shared_with) && item.shared_with.length > 0
                      ? item.shared_with[0]
                      : null;

                    return (
                      <motion.div
                        key={item.uuid}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={isPending ? undefined : () => openBook(item.uuid)}
                        className={`group bg-white rounded-2xl p-4 border border-gray-100 hover:border-[#3DA7B5]/30 hover:shadow-lg transition-all duration-300 relative overflow-hidden ${isPending ? '' : 'cursor-pointer'
                          }`}
                      >
                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-gray-50 to-transparent rounded-bl-full -mr-8 -mt-8 pointer-events-none group-hover:from-[#3DA7B5]/5 transition-colors" />

                        <div className="flex items-center justify-between relative z-10">
                          <div className="flex items-center space-x-4 flex-1 min-w-0">
                            {/* Icon Box */}
                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm transition-colors ${isPending ? "bg-amber-100 text-amber-600" : "bg-[#eff6f7] text-[#3DA7B5] group-hover:bg-[#3DA7B5] group-hover:text-white"
                              }`}>
                              {isPending ? <FaCheck className="w-5 h-5" /> : <FiBook className="w-5 h-5" />}
                            </div>

                            <div className="flex-1 min-w-0">
                              <h3 className="text-base font-bold text-gray-800 truncate group-hover:text-[#3DA7B5] transition-colors leading-tight">
                                {item.title || "Anı Defteri"}
                              </h3>
                              <p className="text-xs text-gray-500 mt-1 truncate font-medium flex items-center gap-1">
                                {item.owner?.name ? (
                                  <>
                                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 group-hover:bg-[#3DA7B5] transition-colors" />
                                    {item.owner.name}
                                  </>
                                ) : (
                                  "Sahip"
                                )}
                              </p>
                            </div>
                          </div>

                          {isPending && pendingShare ? (
                            <div className="flex items-center space-x-2 flex-shrink-0 ml-3" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={async () => {
                                  if (!pendingShare.share_id) return;
                                  setUpdatingShare(pendingShare.share_id);
                                  try {
                                    const response = await updateMemoryBookShareStatus(pendingShare.share_id, 1); // 1 = kabul et
                                    if (response && response.success) {
                                      Notiflix.Notify.success("Paylaşım kabul edildi!");
                                      await fetchList();
                                    }
                                  } catch (error) {
                                    console.error("Paylaşım kabul edilirken hata:", error);
                                    Notiflix.Notify.failure(
                                      error.response?.data?.message || "Paylaşım kabul edilirken bir hata oluştu!"
                                    );
                                  } finally {
                                    setUpdatingShare(null);
                                  }
                                }}
                                disabled={updatingShare === pendingShare.share_id}
                                className="p-2.5 bg-green-500 hover:bg-green-600 active:bg-green-700 text-white rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Kabul Et"
                              >
                                {updatingShare === pendingShare.share_id ? <div className="animate-spin w-4 h-4 border-2 border-white/50 border-t-white rounded-full" /> : <FaCheck className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={async () => {
                                  if (!pendingShare.share_id) return;
                                  setUpdatingShare(pendingShare.share_id);
                                  try {
                                    const response = await updateMemoryBookShareStatus(pendingShare.share_id, 2); // 2 = sil
                                    if (response && response.success) {
                                      Notiflix.Notify.success("Paylaşım reddedildi!");
                                      await fetchList();
                                    }
                                  } catch (error) {
                                    console.error("Paylaşım reddedilirken hata:", error);
                                    Notiflix.Notify.failure(
                                      error.response?.data?.message || "Paylaşım reddedilirken bir hata oluştu!"
                                    );
                                  } finally {
                                    setUpdatingShare(null);
                                  }
                                }}
                                disabled={updatingShare === pendingShare.share_id}
                                className="p-2.5 bg-red-white text-red-500 border border-red-100 hover:bg-red-50 hover:border-red-200 active:bg-red-100 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Sil"
                              >
                                <FaTimes className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2 flex-shrink-0 ml-3 opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-2 group-hover:translate-x-0">
                              <div className="w-8 h-8 rounded-full bg-[#3DA7B5]/10 flex items-center justify-center text-[#3DA7B5]">
                                <FiChevronRight className="w-5 h-5" />
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="h-20"></div>
      </div>
    </div>
  );
}